<template>
  <div class="entry">
    <div class="city-container__header">
      <uikit-autocomplete
        name="city"
        class="city-input input-mobile"
        placeholder="Rechercher une ville"
        :search="handleCitySearch"
        @update:model-value="goToCity"
      >
        <template #prefix>
          <uikit-search-icon class="icon" />
        </template>
      </uikit-autocomplete>
      <div
        id="city-content"
        ref="city-content"
        class="city-container__content"
      >
        <div>
          <div
            v-if="city.hasDistrict && !hasMainDistrict"
            class="city-container__breadcrumb"
          >
            <NuxtLink :to="mainDistrict.link">
              {{ mainDistrict.name }} >
            </NuxtLink>
            <span>{{ cityName }}</span>
          </div>
          <h1 class="city-container__title">
            Investissement locatif à :
            <span>{{ cityName }}</span>
          </h1>
          <p class="city-container__subtitle">
            Découvrez les chiffres du marché immobilier à {{ cityName }}, accédez aux annonces immobilières
            les plus rentables et choisissez la meilleure stratégie d’investissement locatif.
          </p>
          <NuxtLink
            no-prefetch
            class="city-container__rentability"
            to="/rendement-locatif"
          >
            <uikit-button
              type="primary"
              :text-wrap="isMobileWidth"
            >
              Simuler la rentabilité d'un bien
            </uikit-button>
          </NuxtLink>
        </div>
        <div class="city-card">
          <div class="background-img">
            <img
              v-if="hasImg"
              :src="city.imageVilleURL"
              class="city-card__img"
              :alt="cityName"
              loading="lazy"
              @error="handleImgLoadError"
            >
          </div>
          <div class="city-card__content">
            <div class="city-card__title">
              {{ cityName }}
            </div>
            <div
              v-if="city.rendement_moyen"
              class="city-card__calculate"
            >
              <span class="rendement">{{ averageRendement }}</span>
              <span class="label">| Rendement locatif brut</span>
            </div>
          </div>
        </div>
      </div>
      <div class="city-container__stats">
        <div
          v-if="averagePrice || city.rendement_moyen"
          class="city-container__sub-stats"
        >
          <div class="city-container__sub-stat">
            <div class="city-container__numbers">
              {{ averageRendement }}
            </div>
            <div class="city-container__label">
              Rendement brut moyen
            </div>
          </div>
          <div
            v-if="averagePrice"
            class="city-container__sub-stat"
          >
            <div class="city-container__numbers">
              {{ averagePrice }}€ /m²
            </div>
            <div class="city-container__label">
              Prix moyen
            </div>
          </div>
        </div>
        <uikit-autocomplete
          name="city"
          class="city-input input-desktop"
          placeholder="Rechercher une ville"
          :search="handleCitySearch"
          @update:model-value="goToCity"
        >
          <template #prefix>
            <uikit-search-icon class="icon" />
          </template>
        </uikit-autocomplete>
      </div>
    </div>
  </div>
</template>

<script setup>
import { UikitAutocomplete, UikitButton, UikitSearchIcon, useScreenSizeStore } from '@hz/ui-kit'
import { useForm } from 'vee-validate'
import { isMainDistrict, mainDistrictLink } from '@/helpers/citiesWithDistricts'
import { formatNumber } from '../../../helpers/utils'

const props = defineProps({
  city: {
    type: Object,
    required: true,
  },
  cityName: {
    type: String,
    required: true,
  },
})

const loading = ref(false)
const cities = ref([])
const imgLoadError = ref(false)
const hasImg = computed(() => props.city.imageVilleURL || !imgLoadError.value)

const isMobileWidth = computed(() => useScreenSizeStore().isMobileWidth)

const averagePrice = computed(() => {
  if (props.city?.prixm2_moyen?.value?.avgPrix) {
    const thousands = /\B(?=(\d{3})+(?!\d))/g

    return props.city?.prixm2_moyen?.value?.avgPrix.toFixed().toString().replace(thousands, ' ')
  } else return null
})

const averageRendement = computed(() => formatNumber(props.city?.rendement_moyen?.avgRendement, 3, '%'))

const hasMainDistrict = computed(() => isMainDistrict(props.city.code_postal))
const mainDistrict = computed(() => mainDistrictLink(props.city.code_postal))

const initialValues = { city: null }
const { values } = useForm({
  initialValues,
})
const cityDetails = computed(() => cities.value.find((item) => item.label === values.city))

const handleCitySearch = async (query) => {
  loading.value = true
  cities.value = await useCitiesApi().getCities(query)
  loading.value = false

  return cities.value
}

function goToCity() {
  if (!cityDetails.value) {
    return ''
  }

  let postalCode = cityDetails.value?.postalCode

  switch (cityDetails.value?.label?.toLowerCase()) {
    case 'Paris (Toute la ville)'.toLowerCase():
      postalCode = '75000'
      break
    case 'Marseille (Toute la ville)'.toLowerCase():
      postalCode = '13000'
      break
    case 'Lyon (Toute la ville)'.toLowerCase():
      postalCode = '69000'
  }

  useRouter().push({ path: `/ou-investir/villes/${cityDetails.value?.locality}/${postalCode}` })
}

const handleImgLoadError = () => (imgLoadError.value = true)
</script>

<style lang="less" scoped>
.entry {
  width: 100%;
  margin-bottom: 40px;

  .city-container__header {
    background-color: var(--ds-color-primary-25);
    width: 100%;
    display: flex;
    flex-direction: column;
    position: relative;

    .input-mobile {
      display: block;

      :deep(.uikit-input) {
        .input-wrapper {
          border: none;
          border-bottom: 1px solid var(--ds-color-gray-10);
          border-radius: 0;
        }
      }
    }

    .input-desktop {
      display: none;
    }

    .city-container__content {
      flex: 1;
      display: flex;
      flex-direction: row;
      padding: 32px 15px;
      row-gap: 30px;

      .city-container__breadcrumb {
        align-self: start;

        a {
          opacity: 0.5;
          text-decoration: none;
          margin-right: 4px;
        }
      }

      .city-container__title {
        font-weight: var(--ds-weight-semi-bold);
        font-size: 38px;

        span {
          color: var(--ds-color-primary-100);
        }
      }

      .city-container__subtitle {
        font-size: 18px;
      }

      .city-card {
        @media @bp-mobile {
          display: none;
        }
        position: relative;
        max-width: 292px;
        width: 100%;

        .background-img {
          position: relative;
          height: 246px;
          border-radius: 10px;

          &:after {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            border-radius: 10px;
            background: linear-gradient(rgb(0 0 0 / 0%), rgb(0 0 0 / 80%));
          }
        }

        .img,
        .city-card__img {
          border-radius: 10px;
          width: 100%;
          max-width: 450px;
          height: 246px;
        }

        .city-card__img {
          object-fit: cover;
        }

        .city-card__content {
          position: absolute;
          bottom: 0px;
          margin: 20px;

          .city-card__title {
            font-size: 34px;
            font-weight: var(--ds-weight-semi-bold);
            color: var(--ds-color-white-100);
          }

          .city-card__calculate {
            margin-top: 7px;

            .rendement {
              font-weight: var(--ds-weight-semi-bold);
              font-size: 20px;
              color: var(--ds-color-white-100);
            }

            .label {
              color: var(--ds-color-gray-50);
              font-weight: 400;
              font-size: 16px;
              margin-left: 5px;
            }
          }
        }

        @media @bp-desktop {
          .city-card__content {
            .city-card__description,
            .city-card__calculate {
              font-size: 18px;
            }
          }
        }
      }
    }

    .city-container__stats {
      display: flex;
      flex-wrap: wrap;

      .city-container__sub-stats {
        display: grid;
        grid-template-columns: 1fr 1fr;
        flex-wrap: wrap;

        .city-container__sub-stat {
          display: flex;
          flex-direction: column;
          padding: 10px 20px;

          &:first-child {
            background-color: var(--ds-color-primary-100);

            .city-container__numbers {
              color: var(--ds-color-white-100);
            }

            .city-container__label {
              color: var(--ds-color-gray-25);
            }
          }

          &:nth-child(2) {
            border: 1px solid #d5d7db;
            background-color: var(--ds-color-white-100);

            .city-container__numbers {
              color: var(--ds-color-gray-100);
            }

            .city-container__label {
              color: var(--ds-color-gray-50);
            }
          }

          .city-container__label {
            font-weight: 400;
            font-size: 16px;
          }

          .city-container__numbers {
            font-weight: var(--ds-weight-semi-bold);
            font-size: 26px;
            white-space: nowrap;
          }
        }
      }

      .city-container__rentability {
        width: 100%;

        .calculator-icon {
          margin-bottom: -1px;
        }
      }

      .city-container__support {
        width: 100%;
      }
    }

    .city-container__image {
      display: none;
    }
  }

  .city-input {
    &:deep(.input-wrapper) {
      border-width: 1px;
    }

    .icon {
      color: var(--ds-color-gray-50);
      margin-right: 6px;
      margin-left: 4px;
    }
  }

  @media @bp-desktop {
    height: 100% !important;

    .city-container__header {
      flex-direction: column;
      position: relative;
      min-height: 375px;

      .input-mobile {
        display: none;
      }

      .input-desktop {
        display: block;
        width: 400px;

        :deep(.uikit-input .input-wrapper) {
          height: 99px;
          border: 1px solid #e2e2e2;
        }

        :deep(.uikit-input input.inner) {
          height: 100%;
        }
      }

      .city-container__content {
        padding: 60px 50px 80px;
        justify-content: space-between;
        gap: 16px;
        align-items: center;

        & > div {
          max-width: 850px;
        }

        .city-container__title {
          font-size: 42px;
          align-self: flex-start;
        }

        .city-container__subtitle {
          font-weight: 500;
          font-size: 18px;
        }
      }

      .city-container__stats {
        padding: 0 50px;
        position: absolute;
        bottom: -40px;

        & > div:first-child {
          &,
          :deep(.uikit-input .input-wrapper) {
            border-top-left-radius: 4px !important;
            border-bottom-left-radius: 4px !important;
          }
        }

        & > div:last-child {
          &,
          :deep(.uikit-input .input-wrapper) {
            border-radius: 0px 4px 4px 0px;
          }
        }

        .city-container__sub-stats {
          overflow: hidden;

          .city-container__sub-stat {
            padding: 15px 32px;

            &:nth-child(2) {
              border-right: none;
            }
          }
        }
      }

      .city-container__image {
        object-fit: cover;
      }

      .img,
      .city-container__image {
        display: block;
        width: 278px;
        align-self: end;
        height: 242px;
        border: solid 3px #ffffff;
        border-radius: 10px;
        filter: drop-shadow(0px 15px 8px rgba(0, 0, 0, 0.15));
      }
    }
  }
  @media @bp-tablet {
    .city-container__header {
      .city-container__content {
        padding: 40px;
      }

      .city-container__image {
        display: none;
      }
    }
  }
  @media @bp-desktop-xl {
    height: 100% !important;
  }
}
</style>
